import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, DollarSign, CreditCard, Ban, AlertCircle } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import ThemeToggle from '../components/ThemeToggle';
import BrandLogo from '../components/BrandLogo';

const FundingPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
      {/* Header */}
      <header className="border-b border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md sticky top-0 z-40">
        <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
            <BrandLogo size="responsive" data-testid="brand-logo-header" />
            <span className="text-lg sm:text-xl font-bold bg-gradient-to-r from-blue-600 to-blue-800 dark:from-blue-400 dark:to-blue-600 bg-clip-text text-transparent">
              KaiCheckIt
            </span>
          </div>
          <div className="flex items-center gap-2 sm:gap-4">
            <ThemeToggle />
            <Button variant="outline" size="sm" onClick={() => navigate('/transparency')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="max-w-3xl mx-auto">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 rounded-lg bg-amber-100 dark:bg-amber-900/50">
                <DollarSign className="w-6 h-6 text-amber-600 dark:text-amber-400" />
              </div>
              <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300">
                Policy Document
              </Badge>
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-slate-800 dark:text-slate-100 mb-4">
              Funding & Financial Transparency Policy
            </h1>
            <p className="text-slate-600 dark:text-slate-400">
              Last updated: February 2026
            </p>
          </div>

          {/* Content */}
          <div className="space-y-8">
            {/* Purpose */}
            <section>
              <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-4 flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                Purpose
              </h2>
              <Card className="dark:bg-slate-800 dark:border-slate-700">
                <CardContent className="pt-6">
                  <p className="text-slate-700 dark:text-slate-300">
                    KaiCheckIt is committed to transparency regarding its funding sources and financial independence.
                  </p>
                </CardContent>
              </Card>
            </section>

            {/* Funding Sources */}
            <section>
              <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-4 flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                Funding Sources
              </h2>
              <Card className="dark:bg-slate-800 dark:border-slate-700">
                <CardContent className="pt-6 space-y-4">
                  <p className="text-slate-700 dark:text-slate-300">KaiCheckIt may receive funding from:</p>
                  <ul className="list-disc list-inside text-slate-700 dark:text-slate-300 space-y-1">
                    <li>Grants</li>
                    <li>Subscriptions or service fees</li>
                    <li>Research or innovation programs</li>
                  </ul>
                  <p className="text-slate-700 dark:text-slate-300 font-medium border-l-4 border-amber-500 pl-4 bg-amber-50 dark:bg-amber-900/20 py-2">
                    Funding sources do not influence verification outcomes, classifications, or corrections.
                  </p>
                </CardContent>
              </Card>
            </section>

            {/* Advertising & Sponsorship */}
            <section>
              <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-4 flex items-center gap-2">
                <Ban className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                Advertising & Sponsorship
              </h2>
              <Card className="dark:bg-slate-800 dark:border-slate-700">
                <CardContent className="pt-6 space-y-4">
                  <p className="text-slate-700 dark:text-slate-300">
                    KaiCheckIt does not accept advertising or sponsorships that compromise:
                  </p>
                  <ul className="list-disc list-inside text-slate-700 dark:text-slate-300 space-y-1">
                    <li>Editorial independence</li>
                    <li>Methodological integrity</li>
                    <li>Public trust</li>
                  </ul>
                  <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-4 mt-4">
                    <p className="text-slate-700 dark:text-slate-300 text-sm">
                      If advertising or sponsorship is introduced in the future, it will be:
                    </p>
                    <ul className="list-disc list-inside text-slate-600 dark:text-slate-400 text-sm mt-2 space-y-1">
                      <li>Clearly labeled</li>
                      <li>Structurally separated from verification outputs</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </section>

            {/* Conflicts of Interest */}
            <section>
              <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-4 flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                Conflicts of Interest
              </h2>
              <Card className="dark:bg-slate-800 dark:border-slate-700">
                <CardContent className="pt-6 space-y-4">
                  <p className="text-slate-700 dark:text-slate-300">
                    KaiCheckIt maintains internal controls to identify and manage potential conflicts of interest.
                  </p>
                  <p className="text-slate-700 dark:text-slate-300">
                    Any material conflicts affecting verification practices are disclosed transparently.
                  </p>
                </CardContent>
              </Card>
            </section>
          </div>

          {/* Back Link */}
          <div className="mt-10 text-center">
            <Button variant="outline" onClick={() => navigate('/transparency')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Transparency & Accountability
            </Button>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 dark:border-slate-700 bg-white/50 dark:bg-slate-900/50 py-6 mt-12">
        <div className="container mx-auto px-4 text-center text-sm text-slate-500 dark:text-slate-400">
          © 2025 Kaiventive Holdings. All rights reserved.
        </div>
      </footer>
    </div>
  );
};

export default FundingPage;
